import { Button, Card, Flex, Form, FormProps } from 'antd';
import { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

//components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import BoardSettingsComponent from '../components/boardSettings';
import ComponentHeader from 'components/componentHeader/componentHeader';
import NotificationService from 'core/services/notification';
import Icon from 'utils/icon';

// services
import { onCreateBoard } from '../services/boardServices';
import { mapResponseErrors } from 'utils/mapResponseErrors';
import { isRTL } from 'core/services/language';

//models
import { CreateBoardPayload } from '../models/board';

//styles

interface BoardCreatePageProps {}

const formInitialValues: CreateBoardPayload = {
	total_board_members: 0,
	board_term_years: 0,
	maximum_executive_members: null,
	maximum_independent_members: null,
	minimum_quorum_percentage: 50,
	maximum_absence_before_notification: null,
	allow_secretary_update_while_active: '0',
	minimum_notification_days_before_meetings: 1,
	allow_swipe_voting: '0',
	allow_external_guest_invitation: '0',
};

const BoardCreatePage: FunctionComponent<BoardCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [boardForm] = Form.useForm();

	const [confirmModalOpen, setConfirmModalOpen] = useState(false);

	const [isSubmitting, setIsSubmitting] = useState(false);

	const onFinish: FormProps<CreateBoardPayload>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const response = await onCreateBoard(values);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.BOARD_CREATED_SUCCESSFULLY'));

			navigate('/dashboard/actions/boards/' + response.id + '/attach-members');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), error?.message || t('COMMON.SUBMIT_ERROR'));
			if (error.status === 422) mapResponseErrors(error, boardForm);
		}
	};

	return (
		<>
			<Card>
				<ComponentHeader title={t('ACTIONS.BOARD_APPOINTMENT')} subtitle={t('ACTIONS.FILL_TO_APPOINT_BOARD')} />

				<Icon name="registrationStep1" width={280} height={100} style={{ transform: isRTL() ? 'rotateY(180deg)' : 'unset' }} />

				<Form form={boardForm} onFinish={onFinish} initialValues={formInitialValues}>
					<BoardSettingsComponent form={boardForm} />

					<Flex justify="flex-end" className="mt-5">
						<Button
							htmlType="button"
							type="primary"
							onClick={async () => {
								try {
									await boardForm.validateFields();
									setConfirmModalOpen(true);
								} catch (error) {}
							}}>
							{t('COMMON.NEXT')}
						</Button>
					</Flex>
				</Form>
			</Card>

			<ConfirmModalComponent
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={boardForm.submit}
				open={confirmModalOpen}
				isLoading={isSubmitting}
				onDiscard={() => setConfirmModalOpen(false)}
				title={t('COMMON.ALERT')}
				note={t('ACTIONS.BOARD_CREATION_NOTE')}
				noteType="red"
			/>
		</>
	);
};

export default BoardCreatePage;
